import React, { useRef, useEffect, useCallback, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { addPage } from "../../../utility/navigation";
import { handleAddPage } from "../C4S Decommission - Replacement/utils";
import { EditVehicleDetailsSTAMetadata } from "../C4S Decommission - Replacement/EditVehicleDetailsSTAMetadata";
import { ViewVehicleDetailsMetadata } from "../C4S Decommission - Replacement/ViewVehicleDetailsMetadata";
import "./vehicleManagementCss.css";
import { DEVICE_TYPES } from "../C4S Decommission - Replacement/AssignHardwareComponents/AssignHardwareConstants";
import { Icon } from "@vds/icons";

/** Grace period (ms) before closing the menu after mouse leaves. */
const HOVER_GRACE_MS = 300;

/**
 * A lightweight "three dots" menu for Vehicle Management grid rows.
 * Mirrors the Workticket History overlay pattern (OverlayPanel + Options list).
 * Opens on hover (with grace period) as well as on click.
 */
const VehicleActionsMenu = React.memo(function VehicleActionsMenu({
  row,
  disabled = false,
  triggerIcon,
  onReplace,
  showViewDetails = false,
  hideEdit = false,
  hideReplace = false,
  setDeleteModalOpened,
  setSelectedRecordForDelete,
  changeAddresses = [],
}) {
  const moreOperations = useRef(null);
  const buttonRef = useRef(null);
  const hideTimerRef = useRef(null);
  /** Track whether the panel is currently visible so we don't call show() redundantly. */
  const panelVisibleRef = useRef(false);
  /** Stable refs for native event listeners (never change identity across renders). */
  const panelEnterRef = useRef(null);
  const panelLeaveRef = useRef(null);

  /** Cancel any pending hide timer. */
  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  /** Start a grace-period timer that hides the panel after HOVER_GRACE_MS. */
  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => {
      moreOperations.current?.hide?.();
      hideTimerRef.current = null;
    }, HOVER_GRACE_MS);
  };

  // Build stable listener functions once (refs never change identity).
  if (!panelEnterRef.current) {
    panelEnterRef.current = () => cancelHide();
  }
  if (!panelLeaveRef.current) {
    panelLeaveRef.current = () => scheduleHide();
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      cancelHide();
    };
  }, []);
  const handleClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (disabled) return;
    cancelHide();
    // If already visible (e.g. opened by hover), just keep it open.
    // Only show if not already visible.
    if (!panelVisibleRef.current) {
      moreOperations.current?.toggle?.(e);
    }
  };
  /** Show panel on hover (mouseenter on trigger icon). */
  const handleMouseEnter = (e) => {
    if (disabled) return;
    cancelHide();
    if (!panelVisibleRef.current) {
      moreOperations.current?.show?.(e, buttonRef.current);
    }
  };

  /** Start hide grace period when mouse leaves trigger icon. */
  const handleMouseLeave = () => {
    scheduleHide();
  };
  /**
   * When the OverlayPanel becomes visible, attach native mouseenter/mouseleave
   * to its DOM container so hovering the menu keeps it open.
   */
  const handlePanelShow = () => {
    panelVisibleRef.current = true;
    // PrimeReact v7 class-based OverlayPanel stores DOM in overlayRef.current
    const panelEl = moreOperations.current?.overlayRef?.current ?? moreOperations.current?.container ?? moreOperations.current?.el;
    if (panelEl) {
      panelEl.addEventListener("mouseenter", panelEnterRef.current);
      panelEl.addEventListener("mouseleave", panelLeaveRef.current);
    }
  };

  const handlePanelHide = () => {
    panelVisibleRef.current = false;
    cancelHide();
    const panelEl = moreOperations.current?.overlayRef?.current ?? moreOperations.current?.container ?? moreOperations.current?.el;
    if (panelEl) {
      panelEl.removeEventListener("mouseenter", panelEnterRef.current);
      panelEl.removeEventListener("mouseleave", panelLeaveRef.current);
    }
  };

  const closeMenu = () => {
    cancelHide();
    moreOperations.current?.hide?.();
  };

  const handleEdit = () => {
    closeMenu();
    // Edit page navigation (same as View page "Edit" button).
    const pageProps = {
      data: row,
      description: row?.vehicleName ?? row?.vehicle ?? "Vehicle Details",
      mode: "edit",
      returnTo: "vehicle-management",
      changeAddresses,
    };

    try {
      handleAddPage(EditVehicleDetailsSTAMetadata.id, pageProps);
    } catch (err) {
      addPage(EditVehicleDetailsSTAMetadata.id, pageProps);
    }
  };

  const handleReplace = () => {
    closeMenu();
    if (typeof onReplace === "function") {
      onReplace(row);
    }
  };

  const handlePutOnHold = () => {
    closeMenu();
    // TODO: call your put-on-hold function here
    // e.g. onPutOnHold?.(row);
  };

  const handleRelease = () => {
    closeMenu();
    // TODO: call your release function here
    // e.g. onRelease?.(row);
  };
  const handleChangeVehicle = () => {
    closeMenu();
    // TODO: call your change-vehicle function here
    // e.g. onChangeVehicle?.(row);
  };

  const handleViewDetails = () => {
    closeMenu();
    const pageProps = {
      data: row,
      description: row?.vehicleName ?? row?.vehicle ?? "Vehicle Details",
      mode: "view",
      returnTo: "vehicle-management",
      changeAddresses,
    };

    try {
      handleAddPage(ViewVehicleDetailsMetadata.id, pageProps);
    } catch (err) {
      addPage(ViewVehicleDetailsMetadata.id, pageProps);
    }
  };

  const deleteRow = useCallback((row, type) => {
    setDeleteModalOpened(true);
    setSelectedRecordForDelete({ row, type });
  }, []);

  const renderDeleteVehicle = useCallback(
    (row) => {
      const isKeyFob = row.deviceType === DEVICE_TYPES.KEYFOB;
      const isAsset = row.deviceType === DEVICE_TYPES.ASSET;

      // Determine if delete is enabled and delete type
      let isDeleteEnabled = false;
      let deleteType = "";

      if (isKeyFob) {
        isDeleteEnabled = row?.materialMapping?.[0]?.enabled;
        deleteType = "Delete Key Fob";
      } else if (isAsset) {
        const isSubmitted = row?.materialMapping?.[0]?.status?.toLowerCase() === "submitted";
        isDeleteEnabled = !isSubmitted;
        deleteType = "Delete Asset";
      } else {
        // Vehicle
        const allCancelled = row?.materialMapping?.every((material) => material?.wtstatus?.toLowerCase() === "cancelled");
        isDeleteEnabled = !allCancelled && row?.materialMapping?.every((material) => !material?.lineType);
        deleteType = "Delete Vehicle";
      }

      if (!isDeleteEnabled) {
        return null;
      }

      return (
        <div
          onClick={() => {
            deleteRow(row, deleteType);
          }}
          style={{ cursor: "pointer", color: "#E10014", display: "flex", alignItems: "center", gap: "4px", fontSize: "14px" }}
        >
          <Icon
            className="trash-icon"
            aria-label="Delete"
            alt="delete-icon"
            name="trash"
            size="small"
            color="#E10014"
            style={{ cursor: "pointer", outline: "none" }}
          />
          <span>Delete Vehicle</span>
        </div>
      );
    },
    [deleteRow]
  );

  return (
    <>
      <OverlayPanel
        tabIndex={0}
        ref={moreOperations}
        id={`vehicle_actions_${row?.id ?? "row"}`}
        aria-modal="true"
        dismissable={false}
        onShow={handlePanelShow}
        onHide={handlePanelHide}
      >
        <div role="listbox" tabIndex={0}>
          {row?.isShippingBlocked ? (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handleRelease}
              role="option"
              tabIndex={0}
              aria-label="Release"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleRelease();
              }}
            >
              Release
            </div>
          ) : (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handlePutOnHold}
              role="option"
              tabIndex={0}
              aria-label="Put on Hold"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handlePutOnHold();
              }}
            >
              Put on Hold
            </div>
          )}
          {row?.changeVehicleEligible && row?.deviceType === "VEHICLE" ? (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handleChangeVehicle}
              role="option"
              tabIndex={0}
              aria-label="Change Vehicle"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleChangeVehicle();
              }}
            >
              Change Vehicle
            </div>
          ) : null}
          {showViewDetails ? (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handleViewDetails}
              role="option"
              tabIndex={0}
              aria-label="View Details"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleViewDetails();
              }}
            >
              View Details
            </div>
          ) : null}

          {!hideEdit ? (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handleEdit}
              role="option"
              tabIndex={0}
              aria-label="Edit"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleEdit();
              }}
            >
              Edit
            </div>
          ) : null}

          {!hideReplace ? (
            <div
              className={`vehicle-action-menu-option ${disabled ? "vehicle-action-menu-option-disabled" : "vehicle-action-menu-option-enabled"}`}
              onClick={handleReplace}
              role="option"
              tabIndex={0}
              aria-label="Replace"
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleReplace();
              }}
            >
              Replace
            </div>
          ) : null}

          {renderDeleteVehicle(row)}
        </div>
      </OverlayPanel>

      <img
        ref={buttonRef}
        data-testid="vehicle-more-icon"
        className={`vehicle-action-menu-icon ${disabled ? "vehicle-action-menu-icon-disabled" : ""}`}
        alt="more-options-button"
        src={triggerIcon}
        aria-label="more option"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
        role="button"
        aria-controls={`vehicle_actions_${row?.id ?? "row"}`}
        aria-expanded={false}
        aria-haspopup="listbox"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick(e);
        }}
      />
    </>
  );
});

export default VehicleActionsMenu;
